import {BackendFetch} from '@hantera/portal-app'
import parseJson from '@/parse-json'
import parseStatisticsJson from './statistics-json'
import {
  BatchScheduleResult,
  JobDefinition,
  JobQueueStatistics,
  JobQueueStatisticsParams,
  JobStatistics,
  JobStatisticsParams,
  JobStatus,
  ScheduleJobRequest
} from './jobs.types'


export default function jobs(fetch: BackendFetch) {
  return {
    async getJobDefinitions(): Promise<JobDefinition[]> {
      const response = await fetch('/core/resources/job-definitions', {
        method: 'GET',
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const data = parseJson(await response.text())
      return data
    },

    async getStatistics(params?: JobStatisticsParams): Promise<JobStatistics> {
      const queryParams = new URLSearchParams()
      if (params?.jobDefinitionId) {
        queryParams.set('jobDefinitionId', params.jobDefinitionId)
      }
      if (params?.from) {
        queryParams.set('from', params.from.toISOString())
      }
      if (params?.to) {
        queryParams.set('to', params.to.toISOString())
      }

      const queryString = queryParams.toString()
      const url = queryString
        ? `/core/resources/jobs/statistics?${queryString}`
        : '/core/resources/jobs/statistics'

      const response = await fetch(url, {
        method: 'GET',
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const data = parseJson(await response.text())
      return data
    },

    /**
     * Queue health, aggregated across definitions.
     *
     * A queue can be wedged while every definition looks individually healthy, so this is a
     * different question from `getStatistics` rather than a regrouping of it. There is no
     * per-definition filter: a queue aggregates across definitions by design.
     */
    async getQueueStatistics(params?: JobQueueStatisticsParams): Promise<JobQueueStatistics[]> {
      const queryParams = new URLSearchParams()
      if (params?.queue) {
        queryParams.set('queue', params.queue)
      }
      if (params?.from) {
        queryParams.set('from', params.from.toISOString())
      }
      if (params?.to) {
        queryParams.set('to', params.to.toISOString())
      }

      const queryString = queryParams.toString()
      const url = queryString
        ? `/core/resources/jobs/statistics/queues?${queryString}`
        : '/core/resources/jobs/statistics/queues'

      const response = await fetch(url, {
        method: 'GET',
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      // Not parseJson: these buckets feed charts and a saturation division, so they stay numbers.
      return parseStatisticsJson(await response.text()).queues ?? []
    },

    async scheduleJob(request: ScheduleJobRequest): Promise<string> {

      const body: Record<string, unknown> = {
        jobDefinitionId: request.jobDefinitionId,
      }
      if (request.parameters) {
        body.parameters = request.parameters
      }
      if (request.scheduleAt) {
        body.scheduleAt = request.scheduleAt.toISOString()
      }

      const response = await fetch('/core/resources/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const jobId = await response.text()
      return JSON.parse(jobId)
    },

    async cancelJob(jobId: string): Promise<void> {
      const response = await fetch(`/core/resources/jobs/${jobId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }
    },

    async getJob(jobId: string): Promise<JobStatus> {
      const response = await fetch(`/core/resources/jobs/${jobId}`, {
        method: 'GET',
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const data = parseJson(await response.text())
      return data
    },

    async getJobDefinition(definitionId: string, occurrences?: number): Promise<JobDefinition> {
      const query = occurrences != null ? `?occurrences=${occurrences}` : ''
      const response = await fetch(`/core/resources/job-definitions/${definitionId}${query}`, {
        method: 'GET',
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const data = parseJson(await response.text())
      return data
    },

    async updateJobDefinitionSchedule(
      definitionId: string,
      patch: Record<string, unknown>,
    ): Promise<JobDefinition> {
      const response = await fetch(`/core/resources/job-definitions/${definitionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const data = parseJson(await response.text())
      return data
    },

    async scheduleBatch(
      jobDefinitionId: string,
      rows: Record<string, unknown>[],
      onResult: (result: BatchScheduleResult) => void,
      signal?: AbortSignal,
    ): Promise<void> {
      const ndjsonLines = rows.map((parameters) =>
        JSON.stringify({ jobDefinitionId, parameters }),
      )
      const body = ndjsonLines.join('\n') + '\n'

      let response: Awaited<ReturnType<typeof fetch>>
      try {
        response = await fetch('/core/resources/jobs/batch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-ndjson',
          },
          body,
          signal,
        })
      } catch (e) {
        if (signal?.aborted) return
        throw e
      }

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      const reader = response.body?.getReader()
      if (!reader) return

      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          for (const line of lines) {
            const trimmed = line.trim()
            if (!trimmed) continue
            try {
              onResult(JSON.parse(trimmed) as BatchScheduleResult)
            } catch {
              onResult({ error: 'INVALID_RESPONSE' })
            }
          }
        }
      } catch (e) {
        if (signal?.aborted) return
        throw e
      }

      if (buffer.trim()) {
        try {
          onResult(JSON.parse(buffer.trim()) as BatchScheduleResult)
        } catch {
          onResult({ error: 'INVALID_RESPONSE' })
        }
      }
    },
  }
}
